import { ds } from "js/core/models/dataService";
import { Backbone } from "js/vendor";
import getLogger, { LogGroup } from "js/core/logger";
import { app } from "js/namespaces";
import { FeatureType, getUserPlanPermissions, getUserOrgPermissions } from "./planPermissions";
import { getFeatures } from "common/features";

const logger = getLogger(LogGroup.FEATURES);

const Features = Backbone.Model.extend({
    defaults: {
        permissions: Object.freeze({}),
        prevOrgId: null,
        prevTeam: null,
    },

    initialize: function() {
        const loadPermissions = () => {
            this.loadUserPlanPermissions()
                .catch(err => logger.error(err, "[features] error loading user plan permissions"));
        };

        const setCurrentTeams = () => {
            if (!this.currentTeams) {
                this.currentTeams = [];
            }

            // Unsibscribe from old teams
            this.currentTeams.forEach(team => this.stopListening(team, "change"));

            // Reassign current teams and subscribe to them
            this.currentTeams = ds.teams.models;
            this.currentTeams.forEach(team => this.listenTo(team, "change", loadPermissions));
        };

        if (ds.teams) {
            setCurrentTeams();
            this.listenTo(ds.teams, "change", setCurrentTeams);
        }
        if (ds.userPlans) {
            this.listenTo(ds.userPlans, "change", loadPermissions);
        }
    },

    async loadUserPlanPermissions() {
        // Fully load the new permissions map before assigning
        //   so we can avoid race conditions while loading
        let permissions = {};
        permissions["personal"] = await getUserPlanPermissions();

        //ds.teams does not exist for cypress test users
        if (ds.teams && app.user.has("orgs")) {
            for (const orgId of Object.keys(app.user.get("orgs"))) {
                permissions[orgId] = await getUserOrgPermissions(orgId);
            }
        }

        this._permissions = permissions;
    },

    // TODO: if these were persisted we'd fetch them in the usual manner
    async loadFakeElectronUserPlanPermissions() {
        this._permissions = {};
        this._permissions["personal"] = getFeatures("pro");

        if (ds.teams && app.user.has("orgs")) {
            for (const orgId of Object.keys(app.user.get("orgs"))) {
                this._permissions[orgId] = getFeatures("member-licensed");
            }
        }
    },

    isFeatureEnabled(feature, workspaceId) {
        if (workspaceId === undefined) {
            throw new Error("missing workspaceId");
        }
        const rv = this._getWorkspaceFeatures(workspaceId).hasOwnProperty(feature);
        return rv;
    },

    getFeatureProps(feature, workspaceId) {
        if (workspaceId === undefined) {
            throw new Error("missing workspaceId");
        }
        const featureProps = this._getWorkspaceFeatures(workspaceId)[feature];
        if (!featureProps) {
            logger.warn(`${feature} planPermission does not exist for current user. Please check if the ${feature} planPermission is attached to the plan.`, { feature, workspaceId });
        }
        return featureProps || {};
    },

    /** @private */
    _getWorkspaceFeatures(workspaceId) {
        if (workspaceId === undefined) {
            throw new Error("missing workspaceId");
        }
        // presentations from external workspaces go into the personal workspace
        const features = this._permissions[workspaceId];
        if (!features) {
            return this._permissions["personal"];
        }
        return features;
    }
});

export { Features, FeatureType };
